/**
 * QuantumFlowSync.AI - 2FA Setup Page
 * Initial enrollment for Google Authenticator
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Smartphone, Key, CheckCircle, Copy } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { useAuth } from '../contexts/AuthContext';

const TwoFactorSetupPage = () => {
  const navigate = useNavigate();
  const { setup2FA, enable2FA } = useAuth();
  
  const [step, setStep] = useState('loading'); // 'loading' | 'scan' | 'verify' | 'success'
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [manualKey, setManualKey] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadQRCode();
  }, []);

  const loadQRCode = async () => {
    try {
      const result = await setup2FA();
      setQrCodeUrl(result.qrCodeUrl);
      setManualKey(result.secret);
      setStep('scan');
    } catch (err) {
      setError(err.message || 'Failed to load 2FA setup');
      setStep('scan');
    }
  };

  const handleCopyKey = () => {
    navigator.clipboard.writeText(manualKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await enable2FA(verificationCode);
      setStep('success');
      setTimeout(() => {
        navigate('/admin/dashboard');
      }, 2000);
    } catch (err) {
      setError(err.message || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex items-center justify-center mb-4">
            <Shield className="h-12 w-12 text-primary" />
          </div>
          <CardTitle className="text-2xl text-center">
            {step === 'success' ? 'Setup Complete!' : 'Enable Two-Factor Authentication'}
          </CardTitle>
          <CardDescription className="text-center">
            {step === 'scan' && 'Scan the QR code with your authenticator app'}
            {step === 'verify' && 'Enter the 6-digit code to verify'}
            {step === 'success' && 'Your account is now protected with 2FA'}
          </CardDescription>
        </CardHeader>

        {step === 'loading' && (
          <CardContent className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </CardContent>
        )}

        {step === 'scan' && (
          <CardContent className="space-y-6">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-4">
              <div className="bg-muted p-4 rounded-lg">
                <h3 className="font-medium mb-2 flex items-center gap-2">
                  <Smartphone className="h-4 w-4" />
                  Step 1: Install an authenticator app
                </h3>
                <p className="text-sm text-muted-foreground">
                  Download Google Authenticator, Authy, or any TOTP-compatible app
                </p>
              </div>

              <div className="bg-muted p-4 rounded-lg space-y-3">
                <h3 className="font-medium flex items-center gap-2">
                  <Key className="h-4 w-4" />
                  Step 2: Scan this QR code
                </h3>
                
                {qrCodeUrl && (
                  <div className="flex justify-center bg-white p-4 rounded">
                    <img src={qrCodeUrl} alt="2FA QR Code" className="w-48 h-48" />
                  </div>
                )}

                <div className="pt-2">
                  <Label className="text-xs text-muted-foreground">Or enter this key manually:</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      value={manualKey}
                      readOnly
                      className="font-mono text-sm"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={handleCopyKey}
                    >
                      {copied ? <CheckCircle className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        )}

        {step === 'scan' && (
          <CardFooter>
            <Button 
              className="w-full" 
              onClick={() => setStep('verify')}
            >
              I've Scanned the Code →
            </Button>
          </CardFooter>
        )}

        {step === 'verify' && (
          <form onSubmit={handleVerify}>
            <CardContent className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="bg-muted p-4 rounded-lg">
                <h3 className="font-medium mb-2 flex items-center gap-2">
                  <Key className="h-4 w-4" />
                  Step 3: Verify your setup
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Enter the 6-digit code from your authenticator app
                </p>

                <div className="space-y-2">
                  <Label htmlFor="verificationCode">Verification Code</Label>
                  <Input
                    id="verificationCode"
                    type="text"
                    placeholder="000000"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    className="text-center text-2xl tracking-widest"
                    maxLength={6}
                    pattern="[0-9]{6}"
                    required
                    autoFocus
                  />
                </div>
              </div>

              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => setStep('scan')}
              >
                ← Back to QR code
              </Button>
            </CardContent>

            <CardFooter>
              <Button 
                type="submit" 
                className="w-full" 
                disabled={loading || verificationCode.length !== 6}
              >
                {loading ? 'Verifying...' : 'Verify & Enable 2FA'}
              </Button>
            </CardFooter>
          </form>
        )}

        {step === 'success' && (
          <CardContent className="space-y-4 text-center py-8">
            <div className="flex justify-center">
              <div className="rounded-full bg-green-100 p-3">
                <CheckCircle className="h-12 w-12 text-green-600" />
              </div>
            </div>
            <div>
              <h3 className="font-semibold text-lg">2FA Enabled Successfully!</h3>
              <p className="text-sm text-muted-foreground mt-2">
                Your account is now protected with two-factor authentication.
              </p>
              <p className="text-xs text-muted-foreground mt-4">
                Redirecting to dashboard...
              </p>
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default TwoFactorSetupPage;
